import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { motion } from "motion/react";
import { Check, X } from "lucide-react";
import AsyncSelect from "react-select/async";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/global/base/dialog";
import { Button } from "@/global/base/button";
import { Input } from "@/global/base/input";
import { Label } from "@/global/base/label";
import { Checkbox } from "@/global/base/checkbox";
import BlobLoader from "../../common/Loader/BlobLoader";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { createTextMotionProps } from "@/shared/textMotion";
import {
  fetchHospitalsList,
  parseUserManagementSaveErrors,
  userToEditFormState,
} from "@/services/userManagement";

const MotionTr = motion("tr");

const hospitalSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 36,
    borderColor: "#e2e8f0",
    boxShadow: "none",
    "&:hover": { borderColor: "#cbd5e1" },
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
};

function DetailField({
  label,
  value,
  valueNode,
  className = "",
  motionDelay = 0,
  textMotionProps,
  animated = true,
}) {
  const valueClassName = "mt-1 wrap-break-word text-base font-bold text-slate-900";
  const content = valueNode ?? value ?? "—";

  return (
    <div className={className}>
      <p className="text-sm text-slate-500">{label}</p>
      {animated ? (
        <motion.div className={valueClassName} {...textMotionProps(motionDelay)}>
          {content}
        </motion.div>
      ) : (
        <div className={valueClassName}>{content}</div>
      )}
    </div>
  );
}

function EditableField({ label, htmlFor, className = "", children }) {
  return (
    <div className={className}>
      <Label htmlFor={htmlFor} className="text-sm text-slate-500">
        {label}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function formatActiveUser(active) {
  if (active === true) return "Yes";
  if (active === false) return "No";
  return "—";
}

function isYesValue(value) {
  if (value === true || value === "Yes") return true;
  if (value === false || value === "No") return false;
  return null;
}

function GrayYesNoIcon({ value }) {
  const yes = isYesValue(value);
  if (yes === null) return null;

  return (
    <span
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-400 text-white"
      aria-hidden
    >
      {yes ? (
        <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
      ) : (
        <X className="h-3.5 w-3.5" strokeWidth={2.5} />
      )}
    </span>
  );
}

function YesNoWithGrayIcon({ value, className = "" }) {
  const yes = isYesValue(value);
  const label = yes === true ? "Yes" : yes === false ? "No" : "—";

  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <GrayYesNoIcon value={value} />
      <span>{label}</span>
    </span>
  );
}

const YES_NO_FIELD_KEYS = new Set(["active_account", "chief_pharmacist"]);

const VIEW_FIELDS = [
  { key: "first_name", label: "First Name", className: "lg:col-span-4", delay: 0.06 },
  { key: "last_name", label: "Second Name", className: "lg:col-span-4", delay: 0.08 },
  { key: "user_type", label: "Role", className: "lg:col-span-4", delay: 0.1 },
  {
    key: "registration_number",
    label: "Registration Number",
    className: "lg:col-span-3",
    delay: 0.12,
  },
  { key: "date_joined", label: "Date Joined", className: "lg:col-span-3", delay: 0.14 },
  { key: "last_logged_in", label: "Last Logged in", className: "lg:col-span-3", delay: 0.16 },
  {
    key: "active_account",
    label: "Active User",
    className: "lg:col-span-3",
    delay: 0.18,
    format: formatActiveUser,
  },
  { key: "email", label: "Email", className: "lg:col-span-6", delay: 0.2 },
  { key: "phone_number", label: "Phone Number", className: "lg:col-span-6", delay: 0.22 },
  {
    key: "chief_pharmacist",
    label: "Chief Pharmacist",
    className: "lg:col-span-4",
    delay: 0.24,
    showWhen: (user) => user?.chief_pharmacist != null && user.chief_pharmacist !== "—",
  },
  {
    key: "pharmacy_type",
    label: "Pharmacy Type",
    className: "lg:col-span-4",
    delay: 0.26,
    showWhen: (user) => user?.pharmacy_type != null && user.pharmacy_type !== "—",
  },
  {
    key: "pharmacy",
    label: "Pharmacy",
    className: "sm:col-span-2 lg:col-span-12",
    delay: 0.28,
    showWhen: (user) => user?.pharmacy != null && user.pharmacy !== "—",
  },
  {
    key: "hospital",
    label: "Hospital",
    className: "sm:col-span-2 lg:col-span-12",
    delay: 0.3,
    showWhen: (user) => user?.hospital != null && user.hospital !== "—",
  },
];

function renderEditField({
  field,
  form,
  user,
  busy,
  updateField,
}) {
  const { key, label, className, format } = field;

  if (key === "first_name") {
    return (
      <EditableField
        key={key}
        label={label}
        htmlFor="detail-first-name"
        className={className}
      >
        <Input
          id="detail-first-name"
          value={form.first_name}
          onChange={(e) => updateField("first_name", e.target.value)}
          required
          disabled={busy}
          data-testid="user-management-detail-first-name-input"
        />
      </EditableField>
    );
  }

  if (key === "last_name") {
    return (
      <EditableField
        key={key}
        label={label}
        htmlFor="detail-last-name"
        className={className}
      >
        <Input
          id="detail-last-name"
          value={form.last_name}
          onChange={(e) => updateField("last_name", e.target.value)}
          required
          disabled={busy}
          data-testid="user-management-detail-last-name-input"
        />
      </EditableField>
    );
  }

  if (key === "user_type") {
    return (
      <DetailField
        key={key}
        label={label}
        value={form.user_type}
        className={className}
        animated={false}
      />
    );
  }

  if (key === "active_account") {
    return (
      <EditableField
        key={key}
        label={label}
        htmlFor="detail-is-active"
        className={className}
      >
        <div className="flex h-9 items-center gap-2">
          <Checkbox
            id="detail-is-active"
            checked={form.is_active}
            onCheckedChange={(checked) => updateField("is_active", checked === true)}
            disabled={busy}
            data-testid="user-management-detail-active-checkbox"
          />
          <Label
            htmlFor="detail-is-active"
            className="inline-flex cursor-pointer items-center gap-2 text-base font-bold text-slate-900"
          >
            <GrayYesNoIcon value={form.is_active} />
            {form.is_active ? "Yes" : "No"}
          </Label>
        </div>
      </EditableField>
    );
  }

  if (key === "chief_pharmacist") {
    return (
      <DetailField
        key={key}
        label={label}
        valueNode={<YesNoWithGrayIcon value={user.chief_pharmacist} />}
        className={className}
        animated={false}
      />
    );
  }

  if (key === "email") {
    return (
      <EditableField key={key} label={label} htmlFor="detail-email" className={className}>
        <Input
          id="detail-email"
          type="email"
          value={form.email}
          onChange={(e) => updateField("email", e.target.value)}
          required
          disabled={busy}
          data-testid="user-management-detail-email-input"
        />
      </EditableField>
    );
  }

  if (key === "phone_number") {
    return (
      <EditableField key={key} label={label} htmlFor="detail-phone" className={className}>
        <Input
          id="detail-phone"
          type="tel"
          value={form.phone_number}
          onChange={(e) => updateField("phone_number", e.target.value)}
          disabled={busy}
          data-testid="user-management-detail-phone-input"
        />
      </EditableField>
    );
  }

  if (key === "hospital") {
    return null;
  }

  return (
    <DetailField
      key={key}
      label={label}
      value={format ? format(user[key]) : user[key]}
      className={className}
      animated={false}
    />
  );
}

const UserDetailModal = ({
  open,
  user,
  loading = false,
  saving = false,
  productActionId = null,
  canChangeUser = false,
  canManageUserMedication = false,
  canViewHospitals = false,
  onClose,
  onSave,
  onActivateProduct,
  onDeactivateProduct,
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const textMotionProps = useMemo(
    () => createTextMotionProps(reducedMotion),
    [reducedMotion]
  );

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [hospitalOption, setHospitalOption] = useState(null);
  const [saveErrors, setSaveErrors] = useState([]);

  const resetEditState = useCallback(() => {
    setIsEditing(false);
    setForm(null);
    setHospitalOption(null);
    setSaveErrors([]);
  }, []);

  useEffect(() => {
    if (!open) resetEditState();
  }, [open, resetEditState]);

  useEffect(() => {
    resetEditState();
  }, [user?.id, resetEditState]);

  const startEditing = useCallback(() => {
    if (!user) return;
    const next = userToEditFormState(user);
    setForm(next);
    if (next?.hospital_id) {
      const raw = user.raw ?? {};
      const name = raw.hospital_data?.name;
      const address = raw.hospital_data?.address?.full_address;
      const label =
        [name, address].filter(Boolean).join(" — ") ||
        (user.hospital !== "—" ? user.hospital : `Hospital #${next.hospital_id}`);
      setHospitalOption({ value: next.hospital_id, label });
    } else {
      setHospitalOption(null);
    }
    setSaveErrors([]);
    setIsEditing(true);
  }, [user]);

  const loadHospitalOptions = useCallback(async (inputValue) => {
    const list = await fetchHospitalsList(inputValue ?? "");
    return list.map((h) => ({
      value: h.id,
      label: [h.name, h.postal_code].filter(Boolean).join(" — "),
    }));
  }, []);

  const updateField = (key, value) => {
    setSaveErrors([]);
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form || saving) return;
    setSaveErrors([]);
    try {
      await onSave?.(form);
      setIsEditing(false);
    } catch (error) {
      setSaveErrors(parseUserManagementSaveErrors(error));
    }
  };

  if (!user) return null;

  const products = Array.isArray(user.products) ? user.products : [];
  const contentKey = `${user.id}-${loading ? "loading" : "ready"}-${isEditing ? "edit" : "view"}`;
  const busy = loading || saving;

  const sectionMotion = {
    initial: reducedMotion ? false : { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: {
      delay: reducedMotion ? 0 : 0.42,
      duration: reducedMotion ? 0 : 0.35,
      ease: [0.22, 1, 0.36, 1],
    },
  };

  const ProductsSection = isEditing ? "div" : motion.div;
  const productsSectionProps = isEditing
    ? { className: "mt-10" }
    : { className: "mt-10", ...sectionMotion, key: `products-${user.id}` };

  const visibleViewFields = VIEW_FIELDS.filter(
    ({ showWhen }) => !showWhen || showWhen(user)
  );

  const showHospitalField = user.hospital != null && user.hospital !== "—";

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen && !busy) onClose();
      }}
    >
      <DialogContent
        hideCloseButton
        overlayClassName="bg-black/40 backdrop-blur-[2px]"
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl border-0 p-0 sm:max-w-4xl"
        data-testid="user-detail-modal"
      >
        <DialogTitle className="sr-only">
          {isEditing ? "Edit user" : "User details"}
        </DialogTitle>

        <form onSubmit={handleSave} className="relative p-8 pt-10">
          {busy && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/80">
              <BlobLoader size={120} showLoadingText={false} />
            </div>
          )}

          <div key={contentKey} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-12">
            {isEditing && form ? (
              <>
                {visibleViewFields.map((field) =>
                  renderEditField({ field, form, user, busy, updateField })
                )}

                {showHospitalField && (
                  <EditableField
                    label="Hospital"
                    htmlFor="detail-hospital"
                    className="sm:col-span-2 lg:col-span-12"
                  >
                    {canViewHospitals ? (
                      <AsyncSelect
                        inputId="detail-hospital"
                        cacheOptions
                        defaultOptions
                        loadOptions={loadHospitalOptions}
                        value={hospitalOption}
                        onChange={(opt) => {
                          setHospitalOption(opt);
                          updateField("hospital_id", opt?.value ?? null);
                        }}
                        isDisabled={busy}
                        placeholder="Search hospital…"
                        styles={hospitalSelectStyles}
                        classNamePrefix="user-detail-hospital"
                      />
                    ) : (
                      <Input
                        id="detail-hospital"
                        value={hospitalOption?.label ?? user.hospital ?? "—"}
                        disabled
                        className="bg-slate-50 text-slate-600"
                      />
                    )}
                  </EditableField>
                )}
              </>
            ) : (
              visibleViewFields.map(({ key, label, className, delay, format }) => (
                <DetailField
                  key={key}
                  label={label}
                  value={
                    YES_NO_FIELD_KEYS.has(key)
                      ? undefined
                      : format
                        ? format(user[key])
                        : user[key]
                  }
                  valueNode={
                    YES_NO_FIELD_KEYS.has(key) ? (
                      <YesNoWithGrayIcon value={user[key]} />
                    ) : undefined
                  }
                  className={className}
                  motionDelay={delay}
                  textMotionProps={textMotionProps}
                />
              ))
            )}
          </div>

          <ProductsSection {...productsSectionProps}>
            <div className="overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-200">
                    <th className="px-4 py-3 font-semibold text-slate-600">
                      {isEditing ? (
                        "Registered Products"
                      ) : (
                        <motion.span {...textMotionProps(0.28)}>
                          Registered Products
                        </motion.span>
                      )}
                    </th>
                    <th className="px-4 py-3 font-semibold text-slate-600">
                      {isEditing ? (
                        "Expiry Date"
                      ) : (
                        <motion.span {...textMotionProps(0.3)}>Expiry Date</motion.span>
                      )}
                    </th>
                    <th className="px-4 py-3 font-semibold text-slate-600">
                      {isEditing ? (
                        "Status"
                      ) : (
                        <motion.span {...textMotionProps(0.32)}>Status</motion.span>
                      )}
                    </th>
                    {canManageUserMedication && (
                      <th className="px-4 py-3 text-right font-semibold text-slate-600">
                        {isEditing ? (
                          "Action"
                        ) : (
                          <motion.span {...textMotionProps(0.34)}>Action</motion.span>
                        )}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {products.length === 0 ? (
                    isEditing ? (
                      <tr className="border-b-2 border-slate-200">
                        <td
                          colSpan={canManageUserMedication ? 4 : 3}
                          className="px-4 py-6 text-center text-slate-500"
                        >
                          No registered products
                        </td>
                      </tr>
                    ) : (
                      <MotionTr
                        className="border-b-2 border-slate-200"
                        {...textMotionProps(0.36)}
                      >
                        <td
                          colSpan={canManageUserMedication ? 4 : 3}
                          className="px-4 py-6 text-center text-slate-500"
                        >
                          No registered products
                        </td>
                      </MotionTr>
                    )
                  ) : (
                    products.map((product, index) => {
                      const isActive = product.active !== false;
                      const productBusy = productActionId === product.id;
                      const rowDelay = 0.36 + index * 0.06;
                      const rowClassName = "last:border-b-2 last:border-slate-200";
                      const rowCells = (
                        <>
                          <td className="px-4 py-3 font-medium text-slate-800">
                            {product.name}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {product.expiry_date ?? "—"}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {isActive ? "Active" : "Deactivated"}
                          </td>
                          {canManageUserMedication && (
                            <td className="px-4 py-3 text-right">
                              {isActive ? (
                                <Button
                                  className="h-6 min-w-[80px] bg-[#E54646] px-2 text-white hover:bg-[#E54646]/80"
                                  disabled={productBusy || busy}
                                  onClick={() =>
                                    onDeactivateProduct?.(user, product)
                                  }
                                  data-testid={`user-management-product-deactivate-${product.id}`}
                                >
                                  {productBusy ? "…" : "Deactivate"}
                                </Button>
                              ) : (
                                <Button
                                  className="h-6 min-w-[80px] bg-[#1E8A68] px-2 text-white"
                                  disabled={productBusy || busy}
                                  onClick={() =>
                                    onActivateProduct?.(user, product)
                                  }
                                  data-testid={`user-management-product-activate-${product.id}`}
                                >
                                  {productBusy ? "…" : "Activate"}
                                </Button>
                              )}
                            </td>
                          )}
                        </>
                      );

                      if (isEditing) {
                        return (
                          <tr
                            key={product.id ?? product.name}
                            className={rowClassName}
                          >
                            {rowCells}
                          </tr>
                        );
                      }

                      return (
                        <MotionTr
                          key={product.id ?? product.name}
                          className={rowClassName}
                          {...textMotionProps(rowDelay)}
                        >
                          {rowCells}
                        </MotionTr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </ProductsSection>

          {saveErrors.length > 0 && (
            <div
              className="mt-8 rounded-md border border-red-200 bg-red-50 px-4 py-3"
              role="alert"
              data-testid="user-detail-save-errors"
            >
              <p className="text-sm font-semibold text-red-800">
                Could not save changes
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-red-700">
                {saveErrors.map((message, index) => (
                  <li key={`${index}-${message}`}>{message}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-10 flex flex-wrap justify-end gap-4">
            {canChangeUser && !isEditing && (
              <button
                type="button"
                onClick={startEditing}
                disabled={busy}
                className="h-8 rounded-md border-2 px-6 font-semibold border-[#3E826D] bg-white text-[#3E826D] transition-colors hover:bg-[#3E826D]/5 disabled:opacity-50"
                data-testid="user-detail-edit"
              >
                Edit
              </button>
            )}
            {canChangeUser && isEditing && (
              <button
                type="button"
                onClick={resetEditState}
                disabled={busy}
                className="h-8 rounded-md border-2 px-6 font-semibold border-[#3E826D] bg-white text-[#3E826D] transition-colors hover:bg-[#3E826D]/5 disabled:opacity-50"
                data-testid="user-detail-cancel-edit"
              >
                Cancel
              </button>
            )}
            {isEditing && (
              <button
                type="submit"
                disabled={busy || !form}
                className="h-8 rounded-md px-6 font-semibold bg-[#3E826D] text-white transition-colors hover:bg-[#52B894] disabled:opacity-50"
                data-testid="user-detail-save"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="h-8 rounded-md px-6 font-semibold bg-[#3E826D] text-white transition-colors hover:bg-[#52B894] disabled:opacity-50"
              data-testid="user-detail-close"
            >
              Close
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  valueNode: PropTypes.node,
  className: PropTypes.string,
  motionDelay: PropTypes.number,
  textMotionProps: PropTypes.func,
  animated: PropTypes.bool,
};

EditableField.propTypes = {
  label: PropTypes.string.isRequired,
  htmlFor: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

UserDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  user: PropTypes.object,
  loading: PropTypes.bool,
  saving: PropTypes.bool,
  productActionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  canChangeUser: PropTypes.bool,
  canManageUserMedication: PropTypes.bool,
  canViewHospitals: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  onActivateProduct: PropTypes.func,
  onDeactivateProduct: PropTypes.func,
};

export default UserDetailModal;
